import React, { useState, useEffect, useMemo } from 'react';
import styles from './jiraSettings.module.css';
import { get, post } from '../../utils/fetch';
import { Form, Row, Col, Button, Select, Switch, Input } from 'antd';
import { message } from 'antd';
import { ConnectPrompt } from './ConnectPrompt';
import { useIntegrationConnection } from './useIntegrationConnection';
import debounce from 'lodash/debounce';

type OptionType = { value: string; label: string };

interface Props {
  projectId: string;
  organizationId: string;
  integration:
    | {
        enabled: boolean;
        id: string;
        settings: {
          site_url: string;
          site_name: string;
          task_list: OptionType;
          project: OptionType;
          new_tasks: boolean;
          sync_task_updates: boolean;
          new_comments: boolean;
          sync_teamwork_comments: boolean;
          teamwork_comment_webhook_id: string;
          sync_teamwork_task_completion: boolean;
          teamwork_task_completion_webhook_id: string;
          done_column: string;
          task_status: OptionType;
        };
      }
    | undefined;
}

const TeamworkSettings: React.FC<Props> = (props: Props) => {
  const [form] = Form.useForm();

  const { integration, organizationId, projectId } = props;
  const [selectedSiteUrl, setSelectedSiteUrl] = useState(
    integration?.settings?.site_url || integration?.settings?.site_name
  );
  const [selectedProject, setSelectedProject] = useState(
    integration?.settings?.project
  );
  const [selectedTaskList, setSelectedTaskList] = useState(
    integration?.settings?.task_list
  );
  const [selectedStatus, setSelectedStatus] = useState(
    integration?.settings?.task_status
  );
  const [autoPost, setAutoPost] = useState(
    integration?.settings?.new_tasks.toString() === 'true'
  );
  const [syncTaskUpdates, setSyncTaskUpdates] = useState(
    integration?.settings?.sync_task_updates?.toString() !== 'false'
  );
  const [newComments, setNewComments] = useState(
    integration?.settings?.new_comments.toString() === 'true'
  );
  const [syncCommentsToTeamwork, setSyncCommentsToTeamwork] = useState(
    integration?.settings?.sync_teamwork_comments.toString() === 'true'
  );
  const [commentWebhookId, setCommentWebhookId] = useState(
    integration?.settings?.teamwork_comment_webhook_id
  );
  const [syncTaskCompletion, setSyncTaskCompletion] = useState(
    integration?.settings?.sync_teamwork_task_completion.toString() === 'true'
  );
  const [taskCompletionWebhookId, setTaskCompletionWebhookId] = useState(
    integration?.settings?.teamwork_task_completion_webhook_id
  );
  const [doneColumn, setDoneColumn] = useState(
    integration?.settings?.done_column
  );
  const [remoteProjectOptions, setRemoteProjectOptions] = useState<
    OptionType[]
  >([]);
  const [remoteTaskListOptions, setTaskListOptions] = useState<OptionType[]>(
    []
  );
  const [remoteStatusOptions, setRemoteStatusOptions] = useState<OptionType[]>(
    []
  );

  const handleProjectSelect = (
    value: string | undefined,
    option: OptionType | OptionType[]
  ) => {
    setSelectedProject(Array.isArray(option) ? option[0] : option);
    setSelectedTaskList(undefined);
    form.setFieldValue('tasklist', undefined);
    setTaskListOptions([]);
    if (value) getTaskList(value);
  };

  const handleTaskListSelect = (
    _value: string | undefined,
    option: OptionType | OptionType[]
  ) => {
    setSelectedTaskList(Array.isArray(option) ? option[0] : option);
  };

  const handleStatusSelect = (
    _value: string | undefined,
    option: OptionType | OptionType[]
  ) => {
    setSelectedStatus(Array.isArray(option) ? option[0] : option);
  };

  const {
    projectIntegrationId,
    isEnabled,
    getConnectedState,
    saveSettings,
    disconnect
  } = useIntegrationConnection({
    organizationId,
    projectId,
    integration,
    integrationCode: 'Teamwork',
    onConnectedStateLoaded: results => {
      if (!results.settings) return;
      setSelectedProject(results.settings.project);
      setSelectedSiteUrl(
        results.settings.site_url || results.settings.site_name
      );
      setAutoPost(results.settings.new_tasks.toString() === 'true');
      setSyncTaskUpdates(
        results.settings.sync_task_updates?.toString() !== 'false'
      );
      setNewComments(results.settings.new_comments.toString() === 'true');
      setSyncCommentsToTeamwork(
        results.settings.sync_teamwork_comments.toString() === 'true'
      );
      setCommentWebhookId(results.settings.teamwork_comment_webhook_id);
      setSyncTaskCompletion(
        results.settings.sync_teamwork_task_completion.toString() === 'true'
      );
      setTaskCompletionWebhookId(
        results.settings.teamwork_task_completion_webhook_id
      );
      setDoneColumn(results.settings.done_column);
      setSelectedStatus(results.settings.task_status);
    }
  });

  const getTeamOptions = async (siteUrl?: string) => {
    if (!integration || !integration?.enabled) return;
    const teamworkUrl = siteUrl || selectedSiteUrl;
    try {
      let results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${
          integration.id
        }&site_name=${encodeURIComponent(teamworkUrl || '')}`
      );
      setRemoteProjectOptions(results);
    } catch (error) {
      message.error('Something went wrong, please try again.');
    }
  };

  const getTeamOptionsDebounced = useMemo(
    () => debounce((siteUrl: string) => getTeamOptions(siteUrl), 2000),
    [integration?.id, organizationId]
  );

  useEffect(() => {
    return () => getTeamOptionsDebounced.cancel();
  }, [getTeamOptionsDebounced]);

  const handleSiteUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const siteUrl = e.target.value;
    setSelectedSiteUrl(siteUrl);

    if (siteUrl) {
      getTeamOptionsDebounced(siteUrl);
    }
  };

  const getTaskList = async (projectId?: string) => {
    if (!integration || !integration?.enabled) return;
    try {
      let results = await get(
        `/organizations/${organizationId}/integrations/teamwork_task_lists?id=${
          integration.id
        }&site_name=${encodeURIComponent(
          selectedSiteUrl || ''
        )}&project_id=${projectId || selectedProject?.value || ''}`
      );
      setTaskListOptions(results);
    } catch (error) {
      message.error('Something went wrong, please try again.');
    }
  };

  const getStatusColumns = async projectId => {
    if (!integration || !integration?.enabled) return;
    try {
      let results = await get(`/projects/full?id=${projectId}`);

      const columns = [
        { id: null, value: '' },
        ...results.columns.map(column => ({
          id: column.id,
          value: column.name
        }))
      ];
      setRemoteStatusOptions(columns);
    } catch (error) {
      message.error('something went wrong, please try again.');
    }
  };

  const checkWebhooks = async () => {
    try {
      await post(
        `/organizations/${organizationId}/integrations/${integration?.id}/project_integrations/handle_webhooks`,
        {
          project_id: projectId,
          site_url: selectedSiteUrl,
          remote_project_id: selectedProject?.value,
          project_integration_id: projectIntegrationId
        }
      );
    } catch (error) {
      message.error('Something went wrong, please try again.');
    }
  };

  useEffect(() => {
    getConnectedState({
      errorMessage: 'Something went wrong, please try again.'
    });

    if (selectedSiteUrl) {
      getTeamOptions(selectedSiteUrl);
    }

    getStatusColumns(projectId);

    if (selectedProject) {
      getTaskList();
    }
  }, []);

  const refreshWebhookIds = async () => {
    try {
      let results = await get(
        `/organizations/${organizationId}/integrations/integration_connection?project_id=${projectId}&integration_code=Teamwork`
      );
      setCommentWebhookId(results.settings.teamwork_comment_webhook_id);
      setTaskCompletionWebhookId(
        results.settings.teamwork_task_completion_webhook_id
      );
    } catch (error) {
      message.error('Something went wrong, please try again.');
    }
  };

  const renderIntegrationSettings = () => {
    return (
      <div>
        <Form
          form={form}
          layout="horizontal"
          autoComplete="off"
          requiredMark={false}
          onFinish={() =>
            saveSettings(
              {
                site_url: selectedSiteUrl,
                project: selectedProject,
                task_list: selectedTaskList,
                new_tasks: autoPost,
                sync_task_updates: syncTaskUpdates,
                new_comments: newComments,
                sync_teamwork_comments: syncCommentsToTeamwork,
                teamwork_comment_webhook_id: commentWebhookId,
                sync_teamwork_task_completion: syncTaskCompletion,
                teamwork_task_completion_webhook_id: taskCompletionWebhookId,
                done_column: doneColumn,
                task_status: selectedStatus
              },
              {
                beforeSave: refreshWebhookIds,
                afterSave: checkWebhooks,
                reloadAfter: true,
                successMessage: 'Settings saved',
                errorMessage: 'Something went wrong, please try again.'
              }
            )
          }
        >
          <div className={styles.jiraRequired}>
            <div className={styles.requiredBubble}>REQUIRED</div>
            <h3>Connect your Teamwork project</h3>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="siteurl"
                  label={
                    <span className={styles.requiredLabel}>
                      <span>*</span>Teamwork URL
                    </span>
                  }
                  className={styles.jiraProject}
                  rules={[
                    { required: true, message: 'Please enter a Teamwork URL.' }
                  ]}
                  initialValue={selectedSiteUrl}
                  extra="Use your full Teamwork URL, for example company.teamwork.com, company.eu.teamwork.com, or a custom Teamwork domain."
                >
                  <Input
                    value={selectedSiteUrl}
                    onChange={handleSiteUrlChange}
                    placeholder="company.teamwork.com"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="project"
                  label={
                    <span className={styles.requiredLabel}>
                      <span>*</span>Teamwork Project
                    </span>
                  }
                  className={styles.jiraProject}
                  rules={[
                    {
                      required: true,
                      message: 'Please select a Teamwork project.'
                    }
                  ]}
                  initialValue={selectedProject?.value}
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder="Select a project"
                    optionFilterProp="label"
                    options={remoteProjectOptions}
                    onChange={handleProjectSelect}
                    disabled={!selectedSiteUrl}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="tasklist"
                  label={
                    <span className={styles.requiredLabel}>
                      <span>*</span>Teamwork Task List
                    </span>
                  }
                  className={styles.jiraProject}
                  rules={[
                    {
                      required: true,
                      message: 'Please select a Teamwork task list.'
                    }
                  ]}
                  initialValue={selectedTaskList?.value}
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder="Select a task list"
                    optionFilterProp="label"
                    options={remoteTaskListOptions}
                    onChange={handleTaskListSelect}
                    disabled={!selectedProject}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className={styles.jiraWorkflows}>
            <h3>Choose your desired workflow</h3>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item label="Automatically post new tasks to Teamwork">
                  <Switch checked={autoPost} onChange={setAutoPost} />
                </Form.Item>
              </Col>
              {autoPost && (
                <Col span={24}>
                  <Form.Item
                    name="statusColumn"
                    label="Create task when status is equal to"
                    className={styles.selectStatus}
                    initialValue={selectedStatus?.value}
                    tooltip="This is an optional field. Select a status to trigger task creation in Teamwork."
                  >
                    <Select
                      placeholder="Select a status"
                      options={remoteStatusOptions}
                      onChange={handleStatusSelect}
                      allowClear
                    />
                  </Form.Item>
                </Col>
              )}
              <Col span={24}>
                <Form.Item label="Automatically sync task detail updates to Teamwork">
                  <Switch
                    checked={syncTaskUpdates}
                    onChange={setSyncTaskUpdates}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item label="Automatically send new BugHerd comments to Teamwork">
                  <Switch checked={newComments} onChange={setNewComments} />
                </Form.Item>
              </Col>
            </Row>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item label="Sync comments from Teamwork to BugHerd">
                  <Switch
                    checked={syncCommentsToTeamwork}
                    onChange={setSyncCommentsToTeamwork}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  label="Update BugHerd status when completed in Teamwork"
                  tooltip="When a task is completed in Teamwork, move it to a particular BugHerd column. To archive the BugHerd task, set this column to 'archive'."
                >
                  <Switch
                    checked={syncTaskCompletion}
                    onChange={setSyncTaskCompletion}
                  />
                </Form.Item>
              </Col>
              {syncTaskCompletion && (
                <Col span={24}>
                  <Form.Item
                    label="Completed column in BugHerd"
                    className={styles.jiraProject}
                    required
                  >
                    <Input
                      value={doneColumn}
                      onChange={e => setDoneColumn(e.target.value)}
                      placeholder="Enter a BugHerd column"
                      required
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </div>

          <Row
            className={styles.jiraEditRow}
            justify="space-between"
            align="middle"
          >
            <Col>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  shape="round"
                  htmlType="submit"
                  className={styles.save}
                >
                  Save
                </Button>
              </Form.Item>
            </Col>
            <Col>
              <Button
                type="text"
                shape="round"
                onClick={() =>
                  disconnect({
                    reloadAfter: true,
                    successMessage: 'Integration disconnected',
                    errorMessage: 'Something went wrong, please try again.'
                  })
                }
                className={styles.disconnectButton}
                danger
              >
                Disconnect
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {isEnabled ? (
        renderIntegrationSettings()
      ) : (
        <ConnectPrompt
          integrationName="teamwork"
          projectId={projectId}
          organizationId={organizationId}
          type="Teamwork"
          display="Teamwork"
          provider="teamwork"
        />
      )}
    </div>
  );
};

export default (props: Props) => <TeamworkSettings {...props} />;
